import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useChatContext } from "../context";
import { prefersReducedMotion, CONVERSATION_PAGE_SIZE, classifyPart, parseAgentPart, parseOpenList, agentPartId, AGENT_PART_TYPES } from "../utils";
import { blockCut } from "../utils/stepUtils";
import type { MessageImage, Citation, StepFrame, ConversationSummary, SendReply, AgentPartData } from "../types";

// Who reports a part the live reader cannot draw. An unknown type is a later backend's; a
// malformed agent part is a bug.
const REPORT_SKIPPED_PART: Record<"unknown" | "malformed", (part: any) => void> = {
    unknown: part => console.warn("[Chatbot] unknown render part", part),
    malformed: part => Sentry.captureException(new Error(`[Chatbot] malformed ${part.type} part`), { extra: { part } }),
};

const trainingAPI = new TrainingAPI();

// ponytail: pacing constants for the "deliberate streaming" prototype. Tune here while evaluating.
// Drip speed ≈ CHARS_PER_TICK / TICK_MS chars per ms. We append a few chars per tick rather than
// one, because one setTimeout per char hits the browser's short-timer clamp and floors the speed.
const CHARS_PER_TICK = 4;
const TICK_MS = 12;

// A streamed reasoning backlog drains at roughly this rate: the tick takes a hundredth of what
// is waiting, with CHARS_PER_TICK as the floor. The remainder shrinks each tick, so the rate
// decays and the tail types at the floor.
const DRIP_TICKS = 100;

// Above this much text in the buffer, the trace stops being typed and starts being appended a
// paragraph at a time. The number is what the character drip can finish inside DRIP_BUDGET_MS:
// 2500ms at the 4-char floor is about 800 characters, so anything past ~1200 can only be typed
// by making the reader wait. Summary v2 sends whole traces of 30,000 characters in ONE frame.
const BLOCK_CHARS = 1200;
// A block is paced to finish within this, however long it is, and appends one step per
// BLOCK_STEP_MS. 30,000 characters become about 20 appends of a paragraph or two.
const DRIP_BUDGET_MS = 2500;
const BLOCK_STEP_MS = 120;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// The full send → SSE-stream → render turn loop, shared by the /chatbot page and the
// floating widget. Must be called under a ChatProvider; all chat state flows through it.
export function useChatTurn(): {
    sendMessage: (text: string, images?: MessageImage[], reply?: SendReply) => Promise<void>;
} {
    const {
        appendNewMessage,
        setOpenQuestionIds,
        conversationId,
        setConversationId,
        setConversations,
        resetConversation,
        setIsTyping,
        startTurn,
        applyStep,
        clearActiveTurn,
        conversationEpoch,
        appendCaptionDelta,
        clearStreamingCaption,
        appendPendingText,
        clearPendingText,
        attachCitationsToLastAnswer,
        setPendingVizType,
        agentModel,
    } = useChatContext();

    // The turn currently streaming, so every assistant row it appends can carry the key the
    // accordion renders under.
    const activeTurnIdRef = useRef<string | null>(null);

    // A turn owns the visible UI, and the pending-text refs below, only while it is the newest
    // send and the user has not switched conversations since it began. conversationEpoch bumps
    // on setConversation and resetConversation and on nothing else, so it is the switch signal.
    // The sequence number covers the other case: clearing isTyping on a switch re-enables the
    // input, so a second send can start while this stream is still open, and both sends share
    // the refs below. epochRef mirrors the prop because async handlers close over a stale render.
    const epochRef = useRef(conversationEpoch);
    epochRef.current = conversationEpoch;
    const sendSeqRef = useRef(0);
    const turnEpochRef = useRef(conversationEpoch);
    const ownsUi = (seq: number) => seq === sendSeqRef.current && epochRef.current === turnEpochRef.current;

    // Ref so async handlers always read the latest value, not a stale closure snapshot.
    const conversationIdRef = useRef(conversationId);
    conversationIdRef.current = conversationId;
    const pendingTextRef = useRef("");
    const pendingCitationsRef = useRef<Citation[]>([]);
    // Scopes attachCitationsToLastAnswer's retroactive patch to this turn's messages, so a
    // stray citations frame can't stamp sources onto a previous turn's answer.
    const turnStartRef = useRef(0);
    // True once any caption delta has streamed this turn, so render(text) doesn't re-drip the same text.
    const captionStreamedRef = useRef(false);
    // The id of the agent part drawn directly before the current render frame. The backend sends
    // the text copy of an agent part right after it, so one id is enough to hide the copy.
    const prevDrawnIdRef = useRef<string | null>(null);
    // True once this turn's turn_done arrived. A stream that ends without one crashed, and the
    // FE then does not know which cards the backend saved: every card closes.
    const turnDoneRef = useRef(false);

    // Drip a string into the UI one character at a time so streaming is visible even when chunks arrive in bursts.
    const dripText = async (text: string, append: (chunk: string) => void) => {
        for (let i = 0; i < text.length; i += CHARS_PER_TICK) {
            append(text.slice(i, i + CHARS_PER_TICK));
            await sleep(TICK_MS);
        }
    };

    // Reasoning is typed out instead of applied on arrival. The agentic paths stream it token by
    // token, but summary v2 sends the whole trace in ONE delta, which would otherwise appear as a
    // wall of text. This drains on a timer rather than awaiting inside the SSE read loop the way
    // dripText does: a long trace must not hold up the fetch frames queued behind it.
    //
    // A step.done for a step still typing is held until the text drains, so the row closes on the
    // last character rather than mid-sentence. Holding it also keeps the done's authoritative
    // `data.reasoning` (the whole trace) from landing early and skipping the animation.
    //
    // A burst bigger than BLOCK_CHARS switches that entry to block mode for good: it lands in
    // paragraphs against a deadline instead of characters against a rate. `block` is sticky
    // because the tail of a drained block is short, and letting it fall back to the character
    // rate there would add seconds to a trace that is meant to be bounded.
    const dripRef = useRef(new Map<string, {
        mid: string; id: string; text: string; end: StepFrame | null;
        block: boolean; deadline: number; nextAt: number;
    }>());
    const dripTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const dripKey = (mid: string, id: string) => `${mid}\n${id}`;

    const stopDrip = () => {
        if (dripTimerRef.current !== null) clearInterval(dripTimerRef.current);
        dripTimerRef.current = null;
    };

    // How much of the buffer this tick takes. The character rate decays with the remainder, which
    // is what makes a stream read as typing. A block instead divides what is left by the ticks
    // left before its deadline, so the rate holds and the whole trace lands on time.
    const dripChunk = (d: { text: string; block: boolean; deadline: number }, now: number): number => {
        if (!d.block) return Math.max(CHARS_PER_TICK, Math.ceil(d.text.length / DRIP_TICKS));
        const stepsLeft = Math.max(1, Math.ceil((d.deadline - now) / BLOCK_STEP_MS));
        return blockCut(d.text, Math.ceil(d.text.length / stepsLeft));
    };

    const dripTick = () => {
        const now = Date.now();
        dripRef.current.forEach((d, key) => {
            if (d.text) {
                // The timer runs at the character cadence, so a block skips the ticks between
                // its own, slower steps.
                if (d.block && now < d.nextAt) return;
                const n = dripChunk(d, now);
                applyStep({ type: "step.delta", message_id: d.mid, step: { id: d.id, ts: now, delta: d.text.slice(0, n) } });
                d.text = d.text.slice(n);
                d.nextAt = now + BLOCK_STEP_MS;
                return;
            }
            // Drained. An entry with no held end belongs to a step that is still streaming; drop it
            // and let the next delta recreate it, so the timer can stop between bursts.
            if (d.end) applyStep(d.end);
            dripRef.current.delete(key);
        });
        if (dripRef.current.size === 0) stopDrip();
    };

    const enqueueDrip = (mid: string, id: string, delta: string) => {
        // The drip is an animation, so a reader who asks for reduced motion gets the trace at
        // once. Nothing else changes: the same text lands in the same record.
        if (prefersReducedMotion()) {
            applyStep({ type: "step.delta", message_id: mid, step: { id, ts: Date.now(), delta } });
            return;
        }
        const key = dripKey(mid, id);
        const entry = dripRef.current.get(key);
        if (entry) {
            entry.text += delta;
            entry.block = entry.block || entry.text.length > BLOCK_CHARS;
        } else {
            // The deadline covers this burst, not the whole step. An entry is dropped as soon as
            // it drains, so the next burst starts its own budget.
            dripRef.current.set(key, {
                mid, id, text: delta, end: null,
                block: delta.length > BLOCK_CHARS, deadline: Date.now() + DRIP_BUDGET_MS, nextAt: 0,
            });
        }
        if (dripTimerRef.current === null) dripTimerRef.current = setInterval(dripTick, TICK_MS);
    };

    // The interval outlives the component otherwise: it writes state on a tree that is gone when
    // the reader leaves the page or closes the widget mid-trace.
    useEffect(() => stopDrip, []);

    // The stream is over, so nothing is left to pace against. Apply the rest at once, or the last
    // rows would sit "running" for a second after the turn ended.
    const flushDrip = () => {
        dripRef.current.forEach(d => {
            if (d.text) applyStep({ type: "step.delta", message_id: d.mid, step: { id: d.id, ts: Date.now(), delta: d.text } });
            if (d.end) applyStep(d.end);
        });
        dripRef.current.clear();
        stopDrip();
    };

    // Flushes any accumulated general-path text (and its buffered citations) into a committed
    // message. Clearing both refs here is load-bearing: without it, ordering drift between the
    // citations frame and this flush would attach the same citations list twice.
    const flushPendingText = () => {
        if (pendingTextRef.current) {
            appendNewMessage({
                id: Date.now(),
                role: "assistant",
                type: "text",
                content: pendingTextRef.current,
                created: Date.now(),
                metadata: { citations: pendingCitationsRef.current, turnId: activeTurnIdRef.current ?? undefined },
            });
            pendingTextRef.current = "";
            pendingCitationsRef.current = [];
            clearPendingText();
        }
    };

    const handleTurnPersisted = (incomingConversationId: number) => {
        flushPendingText();
        clearStreamingCaption();
        if (conversationIdRef.current === null) {
            setConversationId(incomingConversationId);
        }
        // Merge, not replace: the first page brings the new conversation and fresh titles, and
        // the older pages the sidebar already loaded by scrolling stay below it.
        trainingAPI.getChatbotV2Conversations({ limit: CONVERSATION_PAGE_SIZE })
            .then((page: ConversationSummary[]) => {
                const pageIds = new Set(page.map(c => c.id));
                setConversations(prev => [...page, ...prev.filter(c => !pageIds.has(c.id))]);
            })
            .catch(() => message.error("Could not refresh conversation list."));
    };

    const sendMessage = async (text: string, images: MessageImage[] = [], reply?: SendReply) => {
        const seq = ++sendSeqRef.current;
        let streamStarted = false;
        turnEpochRef.current = epochRef.current;
        turnStartRef.current = Date.now();
        const picked = reply?.choices ?? (reply?.choice ? [reply.choice] : []);
        appendNewMessage({
            id: Date.now(),
            role: "user",
            content: text,
            type: "text",
            created: Date.now(),
            // Live turn renders via useChatImage(ref); the cache is primed with the paste
            // thumbnail (ChatInput) so this is instant. The SSE stream never echoes image parts back.
            metadata: {
                ...(images.length && { images }),
                ...(reply && picked.length && { reply: { questionId: reply.replyTo, choices: picked } }),
            },
        });
        setIsTyping(true);
        captionStreamedRef.current = false;
        prevDrawnIdRef.current = null;
        turnDoneRef.current = false;

        try {
            const models = agentModel ? { agent: agentModel } : undefined;
            const response = await trainingAPI.postChatV2Stream({
                input: text, conversationId, models,
                images: images.map(i => ({ ref: i.ref })),
                replyTo: reply?.replyTo, choice: reply?.choice, choices: reply?.choices,
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            streamStarted = true;

            if (!response.body) throw new Error("No response body");
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                if (!ownsUi(seq)) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() ?? "";

                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    const jsonStr = line.slice(6).trim();
                    if (!jsonStr) continue;
                    try {
                        const event = JSON.parse(jsonStr);
                        await handleStreamEvent(event, seq);
                    } catch (e) {
                        console.error("[Chatbot] Failed to parse SSE event:", e);
                    }
                }
            }
        } catch (e) {
            // Once the stream started, the finally owns the error toast.
            if (ownsUi(seq) && !streamStarted) message.error("Something went wrong. Please try again.");
        } finally {
            // A superseded turn owns neither the UI nor the refs below, which the newer turn is
            // already filling. It must leave everything alone.
            if (seq === sendSeqRef.current) {
                if (ownsUi(seq)) {
                    flushDrip();
                    // Flush any un-committed render text — handles turns where turn_done never
                    // fires. It stamps turnId from the ref, so the ref must still be set here.
                    // (This also clears pendingCitationsRef — flushPendingText owns resetting
                    // both refs together.)
                    flushPendingText();
                    // The stream ended with no turn_done, so the FE does not know which cards the
                    // backend saved. A clean end without a throw reaches here too, so the toast
                    // sits here, not in catch.
                    if (streamStarted && !turnDoneRef.current) {
                        setOpenQuestionIds([]);
                        message.error("That answer did not finish. Please ask again.");
                    }
                    setIsTyping(false);
                    clearStreamingCaption();
                    setPendingVizType(null);
                    clearActiveTurn();
                } else {
                    // Orphaned by a conversation switch, with no newer send. Drop this turn's
                    // buffers so the next turn cannot flush them; the switch owns the rest.
                    // The drip queue is dropped, not flushed: its frames belong to a turn the
                    // reader has left, and applying them would write step records into the
                    // conversation now on screen.
                    dripRef.current.clear();
                    stopDrip();
                    pendingTextRef.current = "";
                    pendingCitationsRef.current = [];
                    clearPendingText();
                }
                activeTurnIdRef.current = null;
            }
        }
    };

    const handleStreamEvent = async (event: any, seq: number) => {
        // An orphaned turn owns no UI. Return before any state write, including the reducer.
        if (!ownsUi(seq)) return;

        if (typeof event.type === "string" && event.type.startsWith("step.")) {
            if (typeof event.message_id !== "string") {
                console.warn("[Chatbot] step frame without message_id", event);
                return;
            }
            if (activeTurnIdRef.current !== event.message_id) {
                activeTurnIdRef.current = event.message_id;
                startTurn(event.message_id);
            }
            const frame = event as StepFrame;
            const stepId = typeof frame.step?.id === "string" ? frame.step.id : null;
            if (stepId !== null) {
                if (frame.type === "step.delta") {
                    enqueueDrip(event.message_id, stepId, typeof frame.step.delta === "string" ? frame.step.delta : "");
                    return;
                }
                const pending = dripRef.current.get(dripKey(event.message_id, stepId));
                if (pending && (frame.type === "step.done" || frame.type === "step.error")) {
                    pending.end = frame;
                    return;
                }
            }
            applyStep(frame);
            return;
        }

        if (event.type === "tool_call_request") {
            if (event.name === "QueueRender") {
                // QueueRender can send a list of viz types; the skeleton shows the first.
                const vizType = event.input?.viz_type;
                setPendingVizType(Array.isArray(vizType) ? vizType[0] ?? null : vizType ?? null);
            }
        }

        if (event.type === "render.caption.delta") {
            captionStreamedRef.current = true;
            await dripText(event.delta, appendCaptionDelta);
        }

        if (event.type === "insights_generating") {
            setPendingVizType("insights");
        }

        if (event.type === "render") {
            await handleRenderPart(event.part);
        }

        if (event.type === "summary") {
            clearStreamingCaption();
            setPendingVizType(null);
            flushPendingText();
            appendNewMessage({
                id: Date.now() + Math.random(),
                role: "assistant",
                type: "summary",
                content: "",
                created: Date.now(),
                metadata: {
                    data: event.payload,
                    turnId: activeTurnIdRef.current ?? undefined,
                },
            });
        }

        if (event.type === "citations") {
            const citations: Citation[] = event.citations ?? [];
            if (pendingTextRef.current) {
                // General path: text hasn't been flushed yet — buffer for flushPendingText.
                pendingCitationsRef.current = citations;
            } else {
                // Render/summary paths: the answer is already a committed message — patch it.
                attachCitationsToLastAnswer(citations, turnStartRef.current);
            }
        }

        if (event.type === "no_data") {
            appendNewMessage({
                id: Date.now(),
                role: "assistant",
                type: "text",
                content: event.message ?? "",
                created: Date.now(),
                metadata: { turnId: activeTurnIdRef.current ?? undefined },
            });
        }

        if (event.type === "conversation_created") {
            setConversationId(event.conversation_id);
            conversationIdRef.current = event.conversation_id;
        }

        if (event.type === "turn_done") {
            turnDoneRef.current = true;
            const open = parseOpenList(event.open);
            setOpenQuestionIds(open ?? []);
            // The contract says every turn_done has it. An absent list is a deploy-order skew
            // (old backend), so only a present but malformed value is a bug worth Sentry.
            if (!open && event.open === undefined) console.warn("[Chatbot] turn_done without an open list");
            if (!open && event.open !== undefined) Sentry.captureException(new Error("[Chatbot] turn_done with a malformed open list"), { extra: { open: event.open } });
            handleTurnPersisted(event.conversation_id);
        }

        if (event.type === "error") {
            const text: string = event.error ?? "";
            if (text.includes("conversation_not_found")) {
                message.error("Conversation not found. Starting a new chat.");
                resetConversation();
            } else if (text) {
                // The server's strings are internal, so the coach gets a generic line.
                // console.error keeps the real one for Sentry.
                console.error("[Chatbot] stream error:", text);
                message.error("Something went wrong with that answer. Please try again.");
            }
        }
    };

    // One live render part. A copy returns before any state write. A part the reader cannot draw
    // flushes the pending text, so the copy that follows starts its own bubble, as on a reload.
    const handleRenderPart = async (part: any) => {
        const cls = classifyPart(part, prevDrawnIdRef.current);
        prevDrawnIdRef.current = null;
        if (cls === "copy") return;
        if (cls !== "draw") {
            REPORT_SKIPPED_PART[cls](part);
            flushPendingText();
            return;
        }
        if (part.type === "text") {
            // Caption deltas were a live preview — the render(text) is the authoritative version.
            clearStreamingCaption();
            if (part.content) {
                pendingTextRef.current += part.content;
                // If no caption preview streamed (backend sent text all at once), drip it now so
                // the reveal is still visible; otherwise commit instantly to avoid double-animating.
                if (!captionStreamedRef.current) {
                    await dripText(part.content, appendPendingText);
                } else {
                    appendPendingText(part.content);
                }
            }
            setPendingVizType(null);
            return;
        }
        // Flush any accumulated text before committing the artifact so order is preserved.
        flushPendingText();
        setPendingVizType(null);
        // Only the agent rows keep content: MessageReview reads it first, the UI does not draw it.
        const agent = AGENT_PART_TYPES.has(part.type) ? (parseAgentPart(part) as AgentPartData) : null;
        const row = agent
            ? { drawnId: agentPartId(agent), content: part.content as string, data: agent }
            : { drawnId: null, content: "", data: part.data };
        prevDrawnIdRef.current = row.drawnId;
        appendNewMessage({
            id: Date.now() + Math.random(),
            role: "assistant",
            // "summary_v2" is the persisted part type for what the FE renders as "summary" —
            // mirrors the same remap in utils.ts's reload mapper.
            type: part.type === "summary_v2" ? "summary" : part.type,
            content: row.content,
            created: Date.now(),
            metadata: { data: row.data, title: part.title, turnId: activeTurnIdRef.current ?? undefined },
        });
    };

    return { sendMessage };
}
